import {
  format,
  subDays,
  parse,
  isValid,
  startOfMonth,
  subMonths,
  subWeeks,
  startOfToday,
  endOfToday,
  startOfYesterday,
  endOfYesterday,
  startOfWeek,
  endOfWeek,
  endOfMonth,
  endOfDay,
  startOfDay,
} from 'date-fns';
import { ReactElement } from 'react';
import { ISO_8601_EXTENDED_FORMAT } from '../shared/constants/commonConstants';
import { ReactComponent as InfoCircleOutlined } from '..//assets/images/svg/InformationIcon.svg';
import { Collapse, Typography } from 'antd';
import { DownOutlined } from '@ant-design/icons';
import React from 'react';
import { REPORTING_CONSTANTS } from '../modules/reportingAndAnalytics/reportingUtils/reportingAndAnalyticsConstants';
import {
  BuildReportParams,
  ExistingConfigItem,
  FilterEntry,
  ReportPayload,
} from '../shared/interfaces/reportingAndAnalyticsInterface';
import { boolean } from 'webidl-conversions';

const { Panel } = Collapse;
const { Text } = Typography;

export const getDateRangeWithOffset = (
  input: string | number,
): { startDate: string; endDate: string } => {
  const now = new Date();
  let start: Date;
  let end: Date;

  switch (input) {
    case 'today': {
      start = startOfToday();
      end = endOfToday();
      break;
    }

    case 'yesterday': {
      start = startOfYesterday();
      end = endOfYesterday();
      break;
    }

    case 'tatToday': {
      start = startOfYesterday();
      end = endOfYesterday();
      break;
    }

    case 'last_week': {
      // previous full week, monday → Sunday
      const lastWeek = subWeeks(now, 1);
      start = startOfWeek(lastWeek, { weekStartsOn: 1 });
      end = endOfWeek(lastWeek, { weekStartsOn: 1 });
      break;
    }

    case 'last_month': {
      const lastMonth = subMonths(now, 1);
      start = startOfMonth(lastMonth);
      end = endOfMonth(lastMonth);
      break;
    }

    case 'last_3_months': {
      const threeMonthsAgo = subMonths(now, 3);
      start = startOfMonth(threeMonthsAgo);
      // until end of last completed month
      const lastMonth = subMonths(now, 1);
      end = endOfMonth(lastMonth);
      break;
    }

    default: {
      const days = Number(input);
      if (isNaN(days)) {
        start = startOfToday();
        end = endOfToday();
        return {
          startDate: format(start, ISO_8601_EXTENDED_FORMAT),
          endDate: format(end, ISO_8601_EXTENDED_FORMAT),
        };
      }
      start = subDays(now, days);
      startOfDay(start);
      end = new Date(now);
      endOfDay(end);
    }
  }

  return {
    startDate: format(startOfDay(start), ISO_8601_EXTENDED_FORMAT),
    endDate: format(endOfDay(end), ISO_8601_EXTENDED_FORMAT),
  };
};

export const extractNumber = (str: string): number | null => {
  const match = str?.match(/\d+/);
  return match ? Number(match[0]) : null;
};

type FilterInput = Record<string, string | string[]>;
interface FilterOutput {
  key: string;
  value: string[];
}

export const splitFiltersToObjects = (
  filters: FilterInput,
): { filters: FilterOutput[] } => {
  if (!filters) {
    return { filters: [] };
  }
  const transformed = Object.entries(filters)?.map(([key, value]: any): any => ({
    key,
    value: Array.isArray(value) ? value?.map(String) : [String(value ?? '')],
  }));
  return { filters: transformed };
};

export const buildUpdatedFilters = (
  filters: Record<string, any>,
  configuredPayers: { value: any }[],
  availableStatus: { value: any }[],
): { key: string; value: any[] }[] => {
  if (!filters) {
    return [];
  }
  return Object?.entries(filters)?.map(([key, rawValue]: any): any => {
    let value = Array.isArray(rawValue) ? rawValue : [rawValue];
    if (key === 'payer' && (!value || value.length === 0)) {
      value = configuredPayers?.map((opt: any): boolean => opt?.value) ?? [];
    }
    if (key === 'claimStatus' && (!value || value.length === 0)) {
      value = availableStatus?.map((opt: any): boolean => opt?.value) ?? [];
    }
    return { key, value };
  });
};

export const splitColumnsByVisibility = (
  configList: any[] = [],
): { selectedColumns: string[]; removedColumns: string[] } => {
  const selectedColumns = configList
    ?.filter((item: Record<string, any>): boolean => item?.isVisible)
    ?.map((item: Record<string, any>): string => item?.mappedComponent ?? '');
  const removedColumns = configList
    ?.filter((item: Record<string, any>): boolean => !item?.isVisible)
    ?.map((item: Record<string, any>): string => item?.mappedComponent ?? '');
  return { selectedColumns, removedColumns };
};

export const parse12HourRange = (
  label: string,
): { StartTime: string; EndTime: string } | null => {
  if (!label) {
    return null;
  }
  const normalize = (time: string): string =>
    time?.trim()?.toUpperCase().replace(/\s+/g, '');
  const to24 = (token: string): string | null => {
    const timeParts = token?.match(/^(\d{1,2})(?::?(\d{2}))?(AM|PM)$/i);
    if (!timeParts) {
      return null;
    }
    let hour = parseInt(timeParts[1], 10);
    const minute = timeParts[2] ? timeParts[2].padStart(2, '0') : '00';
    const meridiem = timeParts[3].toUpperCase();
    if (meridiem === 'PM' && hour !== 12) {
      hour += 12;
    }
    if (meridiem === 'AM' && hour === 12) {
      hour = 0;
    }
    return `${hour.toString().padStart(2, '0')}:${minute}`;
  };
  const parts = label.split('-').map(normalize);
  if (parts.length !== 2) {
    return null;
  }
  const start = to24(parts[0]);
  const end = to24(parts[1]);
  if (!start || !end) {
    return null;
  }
  return { StartTime: start, EndTime: end };
};

const buildUpdatedFiltersFromSchedule = (
  schedulerConfig: ExistingConfigItem[] = [],
  scheduleOptions: string[] = [],
  selectedDailyTime: string = '',
): FilterEntry[] => {
  const enabledSet = new Set(
    scheduleOptions?.map(
      (option: string): string => option?.toLowerCase() ?? '',
    ),
  );
  const dailyOverride = selectedDailyTime
    ? parse12HourRange(selectedDailyTime)
    : null;

  const filters: FilterEntry[] = [];

  schedulerConfig?.forEach((config: any): void => {
    if (!config) {
      return;
    }

    const filterType = config.filterType;
    if (filterType?.toLowerCase() === 'daily') {
      // expand each daily time filter into separate entries
      config?.timeFilter?.forEach((timeStr: string): void => {
        const parsed = parse12HourRange(timeStr);
        const isEnabled =
          !!parsed &&
          dailyOverride &&
          parsed?.StartTime === dailyOverride?.StartTime &&
          parsed?.EndTime === dailyOverride?.EndTime &&
          enabledSet.has('daily');

        filters.push({
          FilterType: filterType,
          isEnabled: boolean(isEnabled),
          TimeFilter: parsed,
        });
      });
    } else {
      filters.push({
        FilterType: filterType,
        isEnabled: boolean(enabledSet.has(filterType?.toLowerCase() ?? '')),
        TimeFilter: null,
      });
    }
  });

  return filters;
};

export const buildReportPayload = (
  params: BuildReportParams,
): ReportPayload => {
  const {
    emailList,
    currentModalKey,
    reportingConfig,
    selectedOnRequestFilter = '',
    scheduleOptions = [],
    selectedDailyTime = '',
    onRequestDateRange,
    tabName,
  } = params || {};
  const payload: ReportPayload = { ConfigType: '', tabName };
  switch (currentModalKey) {
    case 'scheduleReport': {
      payload.SchedulerEmail = emailList || [];
      payload.ConfigType = 'Schedule Report';
      payload.Filters = buildUpdatedFiltersFromSchedule(
        reportingConfig?.schedulerFilters,
        scheduleOptions,
        selectedDailyTime,
      );
      break;
    }
    case 'onRequestReport': {
      payload.ConfigType = 'OnRequest Report';
      payload.Filters = [
        {
          FilterType: selectedOnRequestFilter,
          isEnabled: true,
          TimeFilter: null,
        },
      ];
      payload.onRequestReportingEmail = emailList || [];

      if (selectedOnRequestFilter === 'Custom Date Selection') {
        payload.startDate = onRequestDateRange?.startDate;
        payload.endDate = onRequestDateRange?.endDate;
      } else {
        const months = extractNumber(selectedOnRequestFilter) || 0;
        const { startDate, endDate } = getDateRangeWithOffset(months * 30);
        payload.startDate = startDate;
        payload.endDate = endDate;
      }

      break;
    }
    case 'sendAsMail': {
      payload.ReportingEmail = emailList || [];
      payload.ConfigType = 'Share Report';
      break;
    }
    default: {
      payload.ReportingEmail = emailList || [];
      payload.SchedulerEmail = emailList || [];
      payload.ConfigType = 'Unknown';
      payload.Filters = [];
    }
  }
  return payload;
};

interface ConfigItem {
  mappedComponent: string;
  isVisible: boolean;
}

type Mapper = Record<string, string>;

export const transformMappedComponents = (
  config: ConfigItem[] = [],
  mapper: Mapper = {},
  toInternal: boolean = false,
): ConfigItem[] => {
  const reverseMapper: Mapper = Object?.entries(mapper).reduce(
    (acc: any, [key, value]: any): any => {
      acc[value] = key;
      return acc;
    },
    {} as Mapper,
  );
  return config?.map((item: any): any => ({
    ...item,
    mappedComponent: toInternal
      ? reverseMapper[item?.mappedComponent] || item?.mappedComponent
      : mapper[item?.mappedComponent] || item?.mappedComponent,
  }));
};

export const handleScrollToSection = (id: string): void => {
  if (!id) {
    return;
  }
  const element = document?.getElementById(id);
  if (element) {
    element.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
};

export const convertTo12Hour = (time: string): string => {
  if (!time) {
    return '';
  }
  const [hourStr, minuteStr] = time?.split(':');
  let hour = parseInt(hourStr, 10);
  const minute = minuteStr?.padStart(2, '0') || '00';
  const meridian = hour >= 12 ? 'PM' : 'AM';
  hour = hour % 12 || 12;
  return `${hour}${minute !== '00' ? ':' + minute : ''}${meridian}`;
};

export const parseReportingDisplayDate = (
  dateStr: string,
): Date | undefined => {
  const parsed = parse(
    dateStr,
    REPORTING_CONSTANTS.REPORTING_DISPLAY_FORMAT,
    new Date(),
  );
  return isValid(parsed) ? parsed : undefined;
};

export const renderCollapseMenu = (
  activeKey: string | string[],
  setActiveKey: (key: string | string[]) => void,
): ReactElement => {
  return (
    <Collapse
      activeKey={activeKey}
      onChange={(key: any): void => setActiveKey(key)}
      bordered={false}
      expandIcon={(): null => null}
      className="report-schedule-collapse"
    >
      <Panel
        key="1"
        header={
          <div className="report-schedule-header">
            <div className="left-content">
              <InfoCircleOutlined />
              <Text className="heading-text">
                When will you receive the report ?
              </Text>
            </div>
            <DownOutlined
              className={`custom-expand-icon ${
                activeKey === '1' ? 'rotate' : ''
              }`}
            />
          </div>
        }
      >
        <div className="report-frequency-text">
          <strong>Daily:</strong> Every day at your selected time.
          <br />
          <strong>Weekly:</strong> Every Monday at 8 AM.
          <br />
          <strong>Monthly:</strong> 1st of every month at 8 AM.
          <br />
          <strong>Quarterly:</strong> 1st of every 3rd month.
        </div>
      </Panel>
    </Collapse>
  );
};

export function formatMinutesToDHMS(
  totalMinutes: number,
): React.ReactNode {
  if (totalMinutes < 0) { return <span>0 <small>min</small></span>; }

  const days = Math.floor(totalMinutes / (24 * 60));
  const hours = Math.floor((totalMinutes % (24 * 60)) / 60);
  const minutes = totalMinutes % 60;

  return (
    <>
      {days > 0 && (
        <span>
          {days}
          <span className="tat-duration">d</span>
        </span>
      )}
      {hours > 0 && (
        <span>
          {hours}
          <span className="tat-duration">hr</span>
        </span>
      )}
      {minutes > 0 && days === 0 && (
        <span>
          {minutes}
          <span className="tat-duration">min</span>
        </span>
      )}
    </>
  );
}
